import { bundledExtensions } from './bundled';
import { localExtensions } from './dev/localExtensions';

/**
 * Which extension ids are COMPILED INTO THIS BUILD — the single definition,
 * shared by bootstrap.js (what to register, what a cached/published bundle may
 * not shadow) and SettingsModal.js (what may not offer "Uninstall").
 *
 * ── "bundled" is NOT "hostRendered" ──────────────────────────────────────────
 * As of 05.08.2026 all seven shipped extensions declare `hostRendered: true`,
 * so the two sets now coincide. They are still DIFFERENT PROPERTIES, and
 * conflating them is a live bug waiting to happen — the coincidence is the
 * dangerous part, because it makes the wrong key look like it works:
 *
 *   hostRendered  — a MANIFEST FIELD claiming the extension's UI runs in the
 *                   host realm rather than the sandboxed iframe. It is a
 *                   label, and it is written by whoever wrote the manifest.
 *                   bootstrap.js registers marketplace extensions from CACHED
 *                   BUNDLES on disk, each carrying its own manifest, so any
 *                   third-party extension can put `hostRendered: true` on
 *                   itself and have this build believe it. It is a
 *                   DESCRIPTION, and it is attacker-controlled.
 *
 *   bundled       — ships compiled into the app binary (all seven, plus
 *                   anything in dev/localExtensions.js when the dev flag is
 *                   on). It is about DELIVERY: whether the code can be
 *                   removed from — or introduced to — disk at all. The set
 *                   below is derived from static imports of bundled/index.js,
 *                   so no manifest, download, cache or hand-edited
 *                   localStorage blob can add an id to it, and bootstrap.js
 *                   additionally refuses to let a cached bundle shadow a
 *                   compiled-in id. It is a FACT ABOUT THIS BUILD.
 *
 * Every trust decision therefore keys off THIS MODULE, never off
 * `manifest.hostRendered`:
 *   - uninstallability                    (SettingsModal.js)
 *   - the capability-gate exemption       (capabilities/enforce.js)
 *   - the stale per-capability revoke     (store.migrateAllOrNothingRevokes)
 * Uninstallability, for instance, is a question about delivery: db-manager is
 * just as impossible to uninstall as vault is — `unregisterExtension` only
 * ever set a hide-flag, and bootstrap.js re-registers from the compiled-in
 * source on the next launch.
 *
 * ── the dev half ────────────────────────────────────────────────────────────
 * dev/localExtensions.js is registered only under NODE_ENV=development +
 * REACT_APP_BUNDLED_EXTENSIONS=true. The NODE_ENV guard matters: that
 * registration hands capabilities to a NON-curated extension with no consent
 * screen, so a stray flag in a production .env must never activate it. Anything
 * registered that way is compiled in and equally un-uninstallable, so it belongs
 * in this set; when the flag is off nothing is registered and the question never
 * arises.
 *
 * It is NOT curated, though, and that is why the two trust decisions that hand
 * out access — the capability-gate exemption and the all-or-nothing rule — use
 * `isShippedBundledExtension` below and stop at the shipped half. The dev half
 * states its grants explicitly per entry (an entry declaring `[]` gets nothing,
 * whatever its manifest asks for), and an exemption would quietly delete that.
 * The list is currently EMPTY (hello-world was removed 03.08.2026), so this
 * branch contributes nothing today — it is kept for the next in-development
 * extension. Under jest NODE_ENV is 'test', so this is
 * deterministically empty in tests.
 */
export const devBundledExtensions =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_BUNDLED_EXTENSIONS === 'true'
    ? localExtensions
    : [];

/** Every `{ manifest, entrySource, grantedCapabilities }` compiled into this build. */
export const compiledInExtensions = [...bundledExtensions, ...devBundledExtensions];

/** Ids of everything in `compiledInExtensions`. */
export const compiledInExtensionIds = new Set(
  compiledInExtensions.map((ext) => ext.manifest.id)
);

/**
 * True when this id's source ships inside the app binary — i.e. when
 * "uninstall" is not a thing that can happen and must not be offered.
 */
export function isBundledExtension(extensionId) {
  return compiledInExtensionIds.has(extensionId);
}

/** Ids registered by bundled/index.js — the SHIPPED half, without the dev half. */
export const shippedBundledExtensionIds = new Set(
  bundledExtensions.map((ext) => ext.manifest.id)
);

/**
 * True when this id is one of the curated first-party extensions that
 * bundled/index.js registers with `autoGrant(manifest)` — compiled into the
 * binary, capabilities handed over at boot with no consent screen and no
 * per-capability toggle in Settings.
 *
 * This is the key for anything that treats an extension as trusted rather than
 * merely present: `capabilities/enforce.js`'s gate exemption and
 * `store.migrateAllOrNothingRevokes`. Read the header of this file for why a
 * manifest field must never be used instead — in one sentence, a marketplace
 * bundle carries its own manifest and this set cannot be joined from disk.
 *
 * DELIBERATELY NARROWER than `isBundledExtension`, which also covers the
 * dev half. dev/localExtensions.js registers with an EXPLICIT
 * `grantedCapabilities` rather than `autoGrant`, and documents that an entry
 * declaring `[]` gets nothing whatever its manifest asks for. An exemption
 * covering that half would silently hand a dev entry everything and quietly
 * delete the one property that file promises. (Same reasoning, and the same
 * exclusion, as `hasAllOrNothingCapabilities` below.)
 */
export function isShippedBundledExtension(extensionId) {
  return shippedBundledExtensionIds.has(extensionId);
}

/**
 * True when this extension's capabilities are offered in Settings as ONE unit
 * — the extension's own enable switch — instead of a toggle per capability.
 *
 * DORMANT SINCE 05.08.2026, and kept deliberately. db-manager was the only
 * shipped bundled extension that ever declared capabilities; it now declares
 * none (it is labelled `hostRendered` like the other six, and compiled-in code
 * is exempt from the capability gate — see capabilities/enforce.js), so this
 * predicate matches nothing today. It stays because it is the rule that must
 * apply the next time a bundled extension declares a capability, and deleting
 * it would mean rediscovering the argument below from scratch.
 *
 * The question being asked is "was this extension auto-granted without ever
 * showing a consent screen?", and BUNDLED is exactly that set. bundled/index.js
 * registers every first-party extension with `autoGrant(manifest)` — the whole
 * declared list, no prompt — as a documented, deliberate first-party trust
 * decision. Nobody ever consented capability-by-capability, so there is no
 * per-capability consent to withdraw; all a per-capability switch can do is
 * break the extension in a way the user has no way to diagnose (when DB Manager
 * still declared permissions it needed every one of its six: the three engines,
 * secureStorage (the Vault) to hold their passwords, and the two UI capabilities its
 * connection dialog is built from).
 *
 * Third-party marketplace extensions are the exact opposite case and are
 * deliberately excluded: their grants exist ONLY because an explicit consent
 * flow asked for them, and per-capability granularity is the entire point of
 * that flow. They keep a toggle per capability, untouched.
 *
 * Keyed on neither a slug nor a new manifest field, and both omissions are
 * deliberate. A slug would make db-manager a special case when the reasoning
 * is not special to it. A field like `allOrNothing: true` would be a fiction:
 * nothing in the registry, bootstrap or the enforcement layer would understand
 * it, so it would be a note-to-self dressed up as a contract.
 * `permissions.length > 0` is only a guard against rendering an empty section
 * — the six host-rendered built-ins declare `permissions: []` and never reach
 * any of this.
 *
 * THE LIMIT, stated plainly: this treats "auto-granted" as "all-or-nothing".
 * That is true of every bundled extension shipped today — db-manager is the
 * only one with capabilities at all, and all six are required. If a bundled
 * extension ever ships a genuinely OPTIONAL capability, this rule becomes
 * wrong for it, and the answer is a real signal the manifest, bootstrap AND
 * the enforcement layer all honour — not a special case bolted on here.
 * `bundledCapabilityGranularity.test.js` pins that premise so the day it stops
 * being true is a failing test rather than a silently over-broad UI.
 */
export function hasAllOrNothingCapabilities(manifest) {
  if (!manifest) return false;
  // SHIPPED bundled only — deliberately not `isBundledExtension`, which also
  // covers the dev half. The justification for this rule is `bundled/index.js`
  // calling `autoGrant(manifest)` with no consent screen; dev/localExtensions.js
  // registers with an explicit `grantedCapabilities` instead, so a dev entry
  // declaring `[]` would otherwise be handed its full declared set by one
  // off/on of the switch. The premise test cannot catch that either, since
  // NODE_ENV is 'test' under jest and the dev half is empty there.
  return isShippedBundledExtension(manifest.id) && (manifest.permissions || []).length > 0;
}
